import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogActions } from '@angular/material/dialog';
import { of as observableOf, Observable } from 'rxjs';
import { Plan, AccountFeatureTypes } from '../../models/AccountModel';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { PlatformApiService, PlanSearchCriteria, ResultFilter } from '../../services/platform-api.service';
import { Assessment } from '../../models/AssessmentModel';
import { AssessmentDropdownComponent } from 'src/app/shared/assessments-dropdown';
import { InfiniteScrollOption } from 'src/app/shared/infinite-scroll-select.component';

@Component({
  selector: 'cw-ci-admin-plan-edit',
  templateUrl: './plan-edit.component.html',
  styleUrls: []
})
export class PlanEditComponent implements OnInit {
  _plan: Plan;
  assessments: Assessment[];
  plans: Plan[];
  @Output() stateChange: EventEmitter<any> = new EventEmitter();
  @ViewChild(AssessmentDropdownComponent, {static: false}) dropdown: AssessmentDropdownComponent;
  priceLabel: string;
  labelVisible: boolean;
  oldName: string;
  updateSubscriptions = false;
  selectTrialValue: any[] = [];
  selectSubValue: any[] = [];

  @Input() set plan(value: Plan) {
    this._plan = value;
    this.planForm.reset();

    if (this._plan.id) {
      this.oldName = this._plan.name;
      this.planForm.controls.planName.setValue(this._plan.name);
      this.planForm.controls.planTrialPeriodDays.setValue(this._plan.trialPeriodDays);
      this.planForm.controls.planDescription.setValue(this._plan.description);
      this.planForm.controls.planIsPublic.setValue(this._plan.isPublic);
      this.planForm.controls.planExternalProduct.setValue(this._plan.externalProduct);

      for (const feat of AccountFeatureTypes) {
        const planFeature = this._plan.features.find(el => el.featureType === feat.featureType);
        if (planFeature) {
          switch (feat.dataType) {
            case 'number':
              this.planForm.controls[feat.featureType].setValue(+(planFeature.value));
              this.planForm.controls['TRIAL_' + feat.featureType].setValue(+(planFeature.trialValue));
              break;
            case 'array':
              if (planFeature.value) {
                if (feat.featureType == 'ALLOWED_ASSESSMENT_ID') {
                  this._platformApi.getAssessments(new ResultFilter(undefined, undefined, undefined,undefined, {planId: this._plan.id, isInTrial: false}))
                    .subscribe(data => {
                    this.planForm.controls[feat.featureType].setValue(data.results.map(result => {
                        return new InfiniteScrollOption(result.name, result.id, false, true, true);
                    }));
                    data.results.forEach((item)=> {
                      this.selectSubValue.push(new InfiniteScrollOption(item.name, item.id, false, false, true));
                    });
                  });
                } else {
                  this.planForm.controls[feat.featureType].setValue(planFeature.value.split(',').map(el => +el));
                }
              }
              if (planFeature.trialValue) {
                if (feat.featureType == 'ALLOWED_ASSESSMENT_ID') {
                  this._platformApi.getAssessments(new ResultFilter(undefined, undefined, undefined,undefined, {planId: this._plan.id, isInTrial: true}))
                    .subscribe(data => {
                    this.planForm.controls['TRIAL_' + feat.featureType].setValue(data.results.map(result => {
                      return new InfiniteScrollOption(result.name, result.id, false, true, true);
                    }));
                    data.results.forEach((item)=> {
                      this.selectTrialValue.push(new InfiniteScrollOption(item.name, item.id, false, false, true));
                    });
                  });
                } else {
                  this.planForm.controls['TRIAL_' + feat.featureType].setValue(planFeature.trialValue.split(',').map(el => +el));
                }
              }
              break;
            default:
              this.planForm.controls[feat.featureType].setValue(planFeature.value);
              this.planForm.controls['TRIAL_' + feat.featureType].setValue(planFeature.trialValue);
          }
        }
      }
    } else {
      for (const feat of AccountFeatureTypes) {
        if (feat.dataType == 'boolean') {
          this.planForm.controls[feat.featureType].setValue(false);
          this.planForm.controls['TRIAL_' + feat.featureType].setValue(false);
        }
      }
    }
  }

  get columnsToDisplay() {
    return ['feature', 'trial', 'subscription'];
  }

  planForm = new FormGroup ({
    planName: new FormControl('', [Validators.required]),
    planTrialPeriodDays: new FormControl(''),
    planDescription: new FormControl('', [Validators.required]),
    planIsPublic: new FormControl(''),
    planExternalProduct: new FormControl(''),
    assessmentType: new FormControl('', [])
  });

  constructor(private _platformApi: PlatformApiService, public dialog: MatDialog) {
    for (const feat of AccountFeatureTypes) {
      if (feat.dataType == 'boolean') {
        this.planForm.addControl(feat.featureType, new FormControl(false));
        this.planForm.addControl('TRIAL_' + feat.featureType, new FormControl(false));
      } else {
        this.planForm.addControl(feat.featureType, new FormControl(''));
        this.planForm.addControl('TRIAL_' + feat.featureType, new FormControl(''));
      }
    }
  }

  ngOnInit() {
    this._platformApi.getPlans(new PlanSearchCriteria(true)).subscribe(plans => {
      this.plans = plans.results;
    })
  }

  getAccountFeatureTypes() {
    return AccountFeatureTypes;
  }

  onStateChange(event) {
    if (event.state == 'SELECTED_ASSESSMENTS') {
        const element = event.assessmentType;
        let allowedAssessment;
        let assessmentValues;
        let newValues;
        switch (true) {
          case (element == 'TRIAL_ALLOWED_ASSESSMENT_ID'):
            allowedAssessment = this.planForm.controls[element];
            assessmentValues = this.selectTrialValue;
            break;
          case (element == 'SUB_ALLOWED_ASSESSMENT_ID'):
            allowedAssessment = this.planForm.controls.ALLOWED_ASSESSMENT_ID;
            assessmentValues = this.selectSubValue;
            break;
          default:
            allowedAssessment = this.planForm.controls[element];
            assessmentValues = this.selectTrialValue;
        }

        if (allowedAssessment.value) {
          if (Array.isArray(allowedAssessment.value)) {
            newValues = this.selectedValues(event, allowedAssessment.value);
          } else {
            newValues = this.selectedValues(event, assessmentValues);
          }
        }

        if(element == 'TRIAL_ALLOWED_ASSESSMENT_ID') {
          this.selectTrialValue = allowedAssessment.value ? newValues : event.item;
        } else {
          this.selectSubValue  = allowedAssessment.value ? newValues :event.item;
        }

        allowedAssessment.setValue(allowedAssessment.value? newValues : event.item);
    } else {
      if (event.assessmentType == 'TRIAL_ALLOWED_ASSESSMENT_ID') {
        const trialValue = this.planForm.controls['TRIAL_'+'ALLOWED_ASSESSMENT_ID'].value.split( /,\s*/ );
        const value = this.selectTrialValue.filter(el=> trialValue.includes(el.description));
        this.selectTrialValue = value;
      } else if (event.assessmentType == 'SUB_ALLOWED_ASSESSMENT_ID') {
        const subValue = this.planForm.controls.ALLOWED_ASSESSMENT_ID.value.split( /,\s*/ );
        const value = this.selectSubValue.filter(el=> subValue.includes(el.description));
        this.selectSubValue = value;
      }
    }
  }

  selectedValues(event , values) {
    let selectedValues;
    if(event.deletedItem == undefined && event.item.length > 0) {
      const latestElement = (event.item).pop();
      selectedValues = values.concat(latestElement);
    } else {
      const index = values.map((item) => { return item.value; }).indexOf(event.deletedItem);
      if(index > -1)
        values.splice(index, 1);
        selectedValues = values;
    }

    return selectedValues;
  }

  savePlan() {
    const features = [];
    for (const feat of AccountFeatureTypes) {
      if (feat.dataType == 'array') {
        if (this.planForm.value[feat.featureType]) {
          let val;
          if (this.planForm.value[feat.featureType]) {
            let selectedValues;
            if (feat.featureType == 'ALLOWED_ASSESSMENT_ID') {
              selectedValues = this.planForm.value.ALLOWED_ASSESSMENT_ID.map(value => value.value);
            } else {
              selectedValues = this.planForm.value[feat.featureType];
            }

            val = selectedValues.reduce((accumulator, curValue) => {
              if (accumulator) {
                return [accumulator, curValue].join(',');
              } else {
                return curValue;
              }
            });
          }

          let trialVal;
          if (this.planForm.value['TRIAL_' + feat.featureType]) {
            let selectedValues;
            if (feat.featureType == 'ALLOWED_ASSESSMENT_ID') {
              selectedValues = this.planForm.value.TRIAL_ALLOWED_ASSESSMENT_ID.map(value => value.value);
            } else {
              selectedValues = this.planForm.value['TRIAL_' + feat.featureType];
            }
            trialVal = selectedValues.reduce((accumulator, curValue) => {
              if (accumulator) {
                return [accumulator, curValue].join(',');
              } else {
                return curValue;
              }
            });
          }

          features.push({featureType: feat.featureType, value: val, trialValue: trialVal});
        }
      } else {
        features.push({featureType: feat.featureType, value: this.planForm.value[feat.featureType], trialValue: this.planForm.value['TRIAL_' + feat.featureType]});
      }
    }

    const planJson = {
      name: this.planForm.controls.planName.value,
      description: this.planForm.controls.planDescription.value,
      trialPeriodDays: this.planForm.controls.planTrialPeriodDays.value,
      isPublic: this.planForm.controls.planIsPublic.value,
      features,
      isActive: this._plan.id ? this._plan.isActive : true,
      externalProduct: this.planForm.controls.planExternalProduct.value,
      nameChanged: this.oldName !== this.planForm.controls.planName.value,
      updateSubscriptions: false
    }
    const observe = new Observable(observer => {
      if (this.planForm.dirty && this._plan.subscriptions > 0) {
        const dialogRef = this.dialog.open(YesNoCancelPromptComponent, {
          panelClass: 'flush-dialog'
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result === 'yes') {
            planJson.updateSubscriptions = true;
          }
          observer.next(result);
        });
      }
      else {
        observer.next('no');
      }
    })
    .subscribe(result => {
      if (!result || result == 'cancel') {
        return;
      }
      this._platformApi.savePlan(this._plan.id, { plan: planJson }).subscribe(
        resp => {
          this._plan = resp;
          this.stateChange.emit({ state: 'CREATESUCCESS' })
        }
      );
    });
  }

  cancelPlan() {
    this.planForm.reset();
    this.stateChange.emit({state: 'EDITCANCEL'});
  }

}
@Component({
  selector: 'cw-ci-yes-no-cancel-prompt',
  template: `
  <div class="dashboard-tile" id="yes-no-cancel-prompt" style="width:400px; max-height:250px; box-sizing:content-box;">
    <div class="tile-header">
      <div class="tile-header-title">{{ title }}</div>
    </div>
    <div class="tile-content" style="text-align: center">
      <h3 style="height: inherit;padding-top: 20px;margin-bottom: 9px;box-sizing: border-box;">{{ message }}</h3>
      <div class="tile-footer centered">
        <button class="mat-raised-button mat-primary" (click)="onClick('yes')">Yes</button>
        <button class="mat-raised-button" (click)="onClick('no')">No</button>
        <button class="mat-raised-button" (click)="onClick('cancel')">Cancel</button>
      </div>
  </div>
  `
})
export class YesNoCancelPromptComponent {

  public result = '';
  public title = 'Prompt';
  public message = 'There are active subscriptions using this plan and features have changed. Do you want to update the features for those active subscriptions?';

  constructor(public dialogRef: MatDialogRef<YesNoCancelPromptComponent>) {
  }

  onClick(value: string): void {
    this.result = value;
    this.dialogRef.close(value);
  }
}
